import { FC, ReactNode, useMemo } from 'react';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { Link } from 'components/link/link';
import {
  DataTable,
  type DataTableColumnDef,
  type FilterPanelRenderProps,
} from 'components/data-table';
import { format8 } from 'utils/number';
import type { DivisionOrderResult } from './types';
import { OperatorCellContent } from './operator-cell';

// `parents` is already paginated/filtered by the BE, so client-side row
// filtering must be a no-op even when column filters update.
const noopFilterFn = () => true;

interface FlatRow extends Record<string, unknown> {
  rowKey: string;
  parent: DivisionOrderResult;
  uwi: string | null;
  importedWellName: string | null;
  importedWellApi: string | null;
  propertyName: string | null;
  propertyNo: string | null;
  product: string | null;
  ownerNumber: string | null;
  ownerName: string | null;
  entityId: number | null;
  entityName: string | null;
  interestType: string | null;
  interest: number | null;
}

interface Props {
  parents: DivisionOrderResult[];
  isLoading?: boolean;
  error?: string | null;
  leftToolbarActions?: ReactNode[];
  rightToolbarActions?: ReactNode[];
  initialColumnFilters?: ColumnFiltersState;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  renderFilterPanel?: (props: FilterPanelRenderProps<FlatRow>) => ReactNode;
}

const columns: DataTableColumnDef<FlatRow>[] = [
  {
    id: 'parentName',
    accessorFn: (r) => r.parent.name,
    header: 'Division Order',
    size: 160,
    cell: ({ row }) => (
      <Link to={row.original.parent.href}>{row.original.parent.name}</Link>
    ),
  },
  {
    id: 'source',
    accessorFn: (r) => r.parent.source,
    header: 'DO Input Source',
    size: 150,
    cell: ({ row }) => {
      const { source, sourceDocument } = row.original.parent;
      if (source === 'Provisioned' && sourceDocument?.href) {
        const title =
          sourceDocument.documentTitle ?? `Document #${sourceDocument.id}`;
        return (
          <Link to={sourceDocument.href} title={title}>
            Auto-Connected
          </Link>
        );
      }
      return source ?? 'Salesforce';
    },
  },
  {
    accessorKey: 'uwi',
    header: 'Property',
    size: 140,
    cell: ({ row }) => {
      const { uwi, importedWellApi, importedWellName } = row.original;
      if (uwi) return <Link to={`/wells/${uwi}`}>{uwi}</Link>;
      // ImportedWell match: no UWI and no /wells page — render the API number
      // (else the name) as plain text.
      return importedWellApi ?? importedWellName ?? '--';
    },
  },
  {
    accessorKey: 'propertyName',
    header: 'Property Name',
    size: 160,
    cell: ({ row }) => row.original.propertyName ?? '--',
  },
  {
    accessorKey: 'propertyNo',
    header: 'Property No.',
    size: 120,
    cell: ({ row }) => row.original.propertyNo ?? '--',
  },
  {
    accessorKey: 'product',
    header: 'Product',
    size: 110,
    cell: ({ row }) => row.original.product ?? '--',
  },
  {
    id: 'operator',
    accessorFn: (r) => r.parent.operator?.name ?? null,
    header: 'Operator',
    size: 200,
    cell: ({ row }) => (
      <OperatorCellContent operator={row.original.parent.operator} />
    ),
  },
  {
    accessorKey: 'ownerNumber',
    header: 'Owner number',
    size: 130,
    cell: ({ row }) => row.original.ownerNumber ?? '--',
  },
  {
    id: 'owner',
    accessorFn: (r) => r.entityName ?? r.ownerName ?? null,
    header: 'Owner',
    size: 200,
    cell: ({ row }) => {
      const { entityId, entityName, ownerName } = row.original;
      if (entityId && entityName) {
        return <Link to={`/entities/${entityId}`}>{entityName}</Link>;
      }
      return ownerName ?? '--';
    },
  },
  {
    accessorKey: 'interestType',
    header: 'Interest type',
    size: 120,
    cell: ({ row }) => row.original.interestType ?? '--',
  },
  {
    accessorKey: 'interest',
    header: 'Interest percentage',
    size: 140,
    meta: { type: 'percent', align: 'right' },
    cell: ({ row }) => format8(row.original.interest),
  },
  {
    id: 'dateReceived',
    accessorFn: (r) => r.parent.dateReceived,
    header: 'Date Received',
    size: 140,
    meta: { type: 'date' },
    cell: ({ row }) => row.original.parent.dateReceived ?? '--',
  },
  {
    id: 'createdByName',
    accessorFn: (r) => {
      if (r.parent.source === 'Provisioned') return 'System';
      return r.parent.createdByName;
    },
    header: 'Created By',
    size: 160,
    cell: ({ row }) => {
      const { source, createdByName } = row.original.parent;
      if (source === 'Provisioned') return 'System';
      return createdByName ?? '--';
    },
  },
  {
    id: 'createdAt',
    accessorFn: (r) => r.parent.createdAt,
    header: 'Created At',
    size: 160,
    meta: { type: 'date' },
    cell: ({ row }) => row.original.parent.createdAt ?? '--',
  },
  {
    id: 'updatedAt',
    accessorFn: (r) => r.parent.updatedAt,
    header: 'Updated At',
    size: 160,
    meta: { type: 'date' },
    cell: ({ row }) => row.original.parent.updatedAt ?? '--',
  },
];

const DivisionOrdersFlatTable: FC<Props> = ({
  parents,
  isLoading = false,
  error = null,
  leftToolbarActions,
  rightToolbarActions,
  initialColumnFilters,
  onFilterChange,
  renderFilterPanel,
}) => {
  const rows = useMemo<FlatRow[]>(() => {
    const flat: FlatRow[] = [];
    parents.forEach((parent) => {
      const items = parent.lineItems ?? [];
      if (items.length === 0) {
        flat.push({
          rowKey: `${parent.id}-empty`,
          parent,
          uwi: null,
          importedWellName: null,
          importedWellApi: null,
          propertyName: null,
          propertyNo: null,
          product: null,
          ownerNumber: null,
          ownerName: null,
          entityId: null,
          entityName: null,
          interestType: null,
          interest: null,
        });
        return;
      }
      items.forEach((li, idx) => {
        flat.push({
          rowKey: `${parent.id}-${li.id || idx}`,
          parent,
          uwi: li.uwi,
          importedWellName: li.importedWellName,
          importedWellApi: li.importedWellApi,
          propertyName: li.propertyName,
          propertyNo: li.propertyNumber,
          product: li.product,
          ownerNumber: li.ownerNumber,
          ownerName: li.crmAccountName,
          entityId: li.entityId,
          entityName: li.entityName,
          interestType: li.interestType,
          interest: li.interest,
        });
      });
    });
    return flat;
  }, [parents]);

  const columnsWithNoopFilter = useMemo(
    () =>
      columns.map<DataTableColumnDef<FlatRow>>((col) => ({
        ...col,
        filterFn: noopFilterFn,
      })),
    []
  );

  return (
    <DataTable<FlatRow>
      data={rows}
      columns={columnsWithNoopFilter}
      getRowId={(row) => row.rowKey}
      isLoading={isLoading}
      error={error}
      initialTableState={{ columnFilters: initialColumnFilters }}
      config={{
        columnReordering: false,
        globalSearch: false,
      }}
      leftToolbarActions={leftToolbarActions}
      rightToolbarActions={rightToolbarActions}
      renderFilterPanel={renderFilterPanel}
      onFilterChange={
        onFilterChange ? (s) => onFilterChange(s.columnFilters) : undefined
      }
    />
  );
};

export { DivisionOrdersFlatTable };
